/**
 * useAdaptivePoll
 *
 * usePoll's adaptive sibling. Polls while the tab is visible and online
 * (NetworkGateway), stretches the interval while `run` reports no change,
 * and on a return to the tab runs again only if the last run is older than
 * `minGapMs`, so flicking between tabs does not refetch. `run` resolves true
 * when the data changed, or 'skipped' when it did not read at all. Returns
 * `trigger()` for events that say the data has moved and should be read now.
 */

import { useCallback, useEffect, useId, useRef } from 'react';
import { NetworkGateway } from '../services/NetworkGateway';
import { createAdaptivePoller, type AdaptivePoller, type BackoffPolicy, type PollResult } from '../polling/adaptivePoller';

export interface UseAdaptivePollOptions {
  enabled?: boolean;
  minGapMs?: number;
  /** Also treat window focus as a return. A window behind another app can stay "visible". */
  resumeOnFocus?: boolean;
}

export function useAdaptivePoll(
  run: () => Promise<PollResult>,
  policy: BackoffPolicy,
  { enabled = true, minGapMs = 0, resumeOnFocus = false }: UseAdaptivePollOptions = {},
): () => Promise<void> {
  const runRef = useRef(run);
  const minGapRef = useRef(minGapMs);
  const pollerRef = useRef<AdaptivePoller | null>(null);
  const id = useId();
  const { baseMs, maxMs, factor } = policy;

  // Declared before the poller effect so the first run already sees the latest values.
  useEffect(() => { runRef.current = run; minGapRef.current = minGapMs; });

  useEffect(() => {
    if (!enabled) return;
    const poller = createAdaptivePoller({ run: () => runRef.current(), policy: { baseMs, maxMs, factor }, minGapMs: () => minGapRef.current });
    pollerRef.current = poller;
    const unregister = NetworkGateway.register(`adaptive-poll:${id}`, {
      onActivate: () => poller.resume(),
      onDeactivate: () => poller.pause(),
    });
    const onFocus = () => { if (!document.hidden) poller.resume(); };
    if (resumeOnFocus) window.addEventListener('focus', onFocus);
    // Bootstrap on visibility alone: navigator.onLine reads false in some iOS
    // PWA contexts with a working connection, and the request copes with real
    // offline anyway.
    if (typeof document === 'undefined' || !document.hidden) poller.resume();
    return () => {
      poller.stop();
      unregister();
      if (resumeOnFocus) window.removeEventListener('focus', onFocus);
      if (pollerRef.current === poller) pollerRef.current = null;
    };
    // The policy is read once here and the gap through a ref; changes go
    // through setPolicy below, so a switch (push confirmed, realtime dropped)
    // does not rebuild the poller and fire an extra read straight away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, enabled, resumeOnFocus]);

  useEffect(() => {
    pollerRef.current?.setPolicy({ baseMs, maxMs, factor });
  }, [baseMs, maxMs, factor]);

  return useCallback(() => pollerRef.current?.trigger() ?? Promise.resolve(), []);
}
